/**
 * Single-thread chat state machine.
 *
 * Holds the local message history (array of objects), drives the SSE
 * stream, reduces incoming events into the history shape the popup
 * renders. Strips `ui_*` fields before posting back to Claude — those
 * are render-only.
 *
 * Exposed as a React context so the floating launcher (which lives in
 * the root layout) and the popup share one store.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  ChatMessage,
  ContentBlock,
  SSEDone,
  SSEError,
  SSEMessageStart,
  SSETextDelta,
  SSEToolAwaitingApproval,
  SSEToolCompleted,
  SSEToolFailed,
  SSEToolRunning,
  SSEToolUse,
  StreamRequest,
  TextBlock,
  ToolUseBlock,
} from '@/types/chat';

interface ChatThreadState {
  open: boolean;
  threadId: string | null;
  history: ChatMessage[];
  streaming: boolean;
  error: string | null;

  setOpen: (v: boolean) => void;
  send: (text: string) => Promise<void>;
  loadThread: (threadId: string, history: ChatMessage[]) => void;
  newThread: () => void;
  approveTool: (toolUseId: string, customRules?: string[], sceneMode?: boolean) => Promise<void>;
  cancelTool: (toolUseId: string) => Promise<void>;
}

const Ctx = createContext<ChatThreadState | null>(null);

function stripUiFields(history: ChatMessage[]): ChatMessage[] {
  return history.map((m) => {
    if (typeof m.content === 'string') return m;
    return {
      role: m.role,
      content: m.content.map((b): ContentBlock => {
        if (b.type !== 'tool_use') return b;
        return {
          type: 'tool_use',
          id: b.id,
          name: b.name,
          input: b.input,
        };
      }),
    };
  });
}

function lastAssistantBlocks(history: ChatMessage[]): ContentBlock[] | null {
  for (let i = history.length - 1; i >= 0; i--) {
    const msg = history[i];
    if (msg && msg.role === 'assistant' && Array.isArray(msg.content)) {
      return msg.content;
    }
  }
  return null;
}

/**
 * Reconstruct `ui_status` / `ui_result` / `ui_error` on tool_use blocks from
 * the persisted tool_result blocks elsewhere in the history.
 *
 * Why: we strip `ui_*` fields before sending to the API (canonical Anthropic
 * shape), so the server's persisted thread has no operator-UI state. When a
 * thread is reloaded (sidebar click / page refresh / new browser tab),
 * `ToolCallCard` would otherwise default every prior tool to `'pending'` —
 * the "Queued / no inline result" symptom even though the call long since
 * completed. Walking the history once on load gives every tool_use back its
 * final UI status by matching `tool_use.id` ↔ `tool_result.tool_use_id`.
 *
 * Tools without a matching tool_result are left alone (renders as 'pending'),
 * which is correct: they were interrupted mid-stream.
 */
function hydrateUiFields(history: ChatMessage[]): ChatMessage[] {
  // Build a map: tool_use_id → { is_error, parsed_content }
  const results = new Map<string, { isError: boolean; parsed: unknown }>();
  for (const msg of history) {
    if (msg.role !== 'user' || !Array.isArray(msg.content)) continue;
    for (const block of msg.content) {
      if (block.type !== 'tool_result') continue;
      let parsed: unknown;
      try {
        parsed = JSON.parse(block.content);
      } catch {
        parsed = block.content;
      }
      results.set(block.tool_use_id, { isError: block.is_error, parsed });
    }
  }

  if (results.size === 0) return history;

  return history.map((m) => {
    if (typeof m.content === 'string') return m;
    return {
      role: m.role,
      content: m.content.map((b): ContentBlock => {
        if (b.type !== 'tool_use') return b;
        const match = results.get(b.id);
        if (!match) return b;
        if (match.isError) {
          const errObj =
            match.parsed &&
            typeof match.parsed === 'object' &&
            'error' in match.parsed
              ? match.parsed.error
              : match.parsed;
          const env =
            errObj && typeof errObj === 'object'
              ? (errObj as { code?: string; message?: string; details?: Record<string, unknown> })
              : { code: 'unknown', message: String(errObj) };
          const code = env.code ?? 'unknown';
          // An operator-declined / superseded gate is a cancellation, not a
          // failure — render it as 'cancelled' so a reloaded thread matches
          // the live cancel UI instead of showing a red error box.
          const status = code === 'operator_cancelled' || code === 'gate_superseded'
            ? 'cancelled'
            : 'failed';
          return {
            ...b,
            ui_status: status,
            ui_error: {
              code,
              message: env.message ?? 'Tool failed',
              ...(env.details ? { details: env.details } : {}),
            },
          };
        }
        return {
          ...b,
          ui_status: 'completed',
          ui_result:
            match.parsed && typeof match.parsed === 'object'
              ? (match.parsed as Record<string, unknown>)
              : { value: match.parsed },
        };
      }),
    };
  });
}

/**
 * Resolve any still-open cost-approval gate before a new turn starts.
 *
 * Sending a new message while a money tool sits at `awaiting_approval` is an
 * implicit decline. Without this, that gated tool_use is replayed to the API
 * with `ui_*` stripped and no `tool_result`, so the server re-persists it
 * bare — on the next reload it hydrates to `pending` and renders as a
 * button-less "Queued" card that can never be actioned. Here we flip each
 * open gate to `cancelled` and append a synthetic `tool_result` so the
 * cancellation is real for both Claude and persistence. Returns the original
 * array untouched when there's nothing open.
 */
function supersedeOpenGates(history: ChatMessage[]): ChatMessage[] {
  const openIds: string[] = [];
  const patched = history.map((m) => {
    if (typeof m.content === 'string') return m;
    return {
      role: m.role,
      content: m.content.map((b): ContentBlock => {
        if (b.type !== 'tool_use' || b.ui_status !== 'awaiting_approval') return b;
        openIds.push(b.id);
        return {
          ...b,
          ui_status: 'cancelled',
          ui_error: {
            code: 'gate_superseded',
            message: 'Superseded by a new request before approval.',
          },
        };
      }),
    };
  });
  if (openIds.length === 0) return history;

  const resolved = new Set<string>();
  for (const m of patched) {
    if (typeof m.content === 'string') continue;
    for (const b of m.content) {
      if (b.type === 'tool_result') resolved.add(b.tool_use_id);
    }
  }
  const closers: ChatMessage[] = openIds
    .filter((id) => !resolved.has(id))
    .map((id) => ({
      role: 'user',
      content: [
        {
          type: 'tool_result',
          tool_use_id: id,
          content: JSON.stringify({
            error: {
              code: 'gate_superseded',
              message: 'Superseded by a new request before approval.',
            },
          }),
          is_error: true,
        },
      ],
    }));
  return [...patched, ...closers];
}

export function ChatThreadProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();

  const newThread = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setThreadId(null);
    setHistory([]);
    setStreaming(false);
    setError(null);
  }, []);

  const loadThread = useCallback(
    (id: string, msgs: ChatMessage[]) => {
      abortRef.current?.abort();
      abortRef.current = null;
      setThreadId(id);
      // Persisted messages don't carry ui_* fields — derive them from the
      // tool_result blocks so completed tools render with their inline result
      // instead of reverting to "Queued".
      setHistory(hydrateUiFields(msgs));
      setStreaming(false);
      setError(null);
    },
    [],
  );

  const send = useCallback(
    async (text: string) => {
      if (!text.trim() || streaming) return;

      const baseHistory: ChatMessage[] = [
        ...supersedeOpenGates(history),
        { role: 'user', content: text },
      ];
      const draftHistory: ChatMessage[] = [
        ...baseHistory,
        { role: 'assistant', content: [] },
      ];
      setHistory(draftHistory);
      setStreaming(true);
      setError(null);

      const ctrl = new AbortController();
      abortRef.current = ctrl;

      const req: StreamRequest = {
        thread_id: threadId,
        history: stripUiFields(baseHistory),
      };

      try {
        await api.chat.stream(
          req,
          (frame) => {
            const data = JSON.parse(frame.data) as unknown;
            setHistory((curr) => reduce(curr, frame.event, data, setThreadId));
          },
          ctrl.signal,
        );
      } catch (err) {
        if ((err as DOMException)?.name === 'AbortError') return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        setHistory((curr) => {
          const next = [...curr];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant' && Array.isArray(last.content)) {
            last.content = [
              ...last.content,
              { type: 'text', text: `\n\nStream failed: ${msg}` },
            ];
          }
          return next;
        });
      } finally {
        setStreaming(false);
        abortRef.current = null;
        void queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
      }
    },
    [history, streaming, threadId, queryClient],
  );

  const approveTool = useCallback(
    async (toolUseId: string, customRules?: string[], sceneMode?: boolean) => {
      if (!threadId || streaming) return;
      setStreaming(true);
      setError(null);
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        await api.chat.approveTool(
          threadId,
          toolUseId,
          (frame) => {
            const data = JSON.parse(frame.data) as unknown;
            setHistory((curr) => reduce(curr, frame.event, data, setThreadId));
          },
          ctrl.signal,
          customRules,
          sceneMode,
        );
      } catch (err) {
        if ((err as DOMException)?.name === 'AbortError') return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      } finally {
        setStreaming(false);
        abortRef.current = null;
        void queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
      }
    },
    [threadId, streaming, queryClient],
  );

  const cancelTool = useCallback(
    async (toolUseId: string) => {
      if (!threadId || streaming) return;
      setError(null);
      try {
        await api.chat.cancelTool(threadId, toolUseId);
        setHistory((curr) => {
          const patched = curr.map((m) =>
            typeof m.content === 'string'
              ? m
              : { ...m, content: patchToolUseInContent(m.content, toolUseId) },
          );
          patched.push({
            role: 'user',
            content: [
              {
                type: 'tool_result',
                tool_use_id: toolUseId,
                content: JSON.stringify({
                  error: {
                    code: 'operator_cancelled',
                    message: 'Operator cancelled the cost-approval prompt.',
                  },
                }),
                is_error: true,
              },
            ],
          });
          return patched;
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
      } finally {
        void queryClient.invalidateQueries({ queryKey: ['chat-threads'] });
      }
    },
    [threadId, streaming, queryClient],
  );

  const value = useMemo<ChatThreadState>(
    () => ({
      open,
      threadId,
      history,
      streaming,
      error,
      setOpen,
      send,
      loadThread,
      newThread,
      approveTool,
      cancelTool,
    }),
    [
      open,
      threadId,
      history,
      streaming,
      error,
      send,
      loadThread,
      newThread,
      approveTool,
      cancelTool,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useChatThread(): ChatThreadState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useChatThread must be used inside ChatThreadProvider');
  return v;
}


function reduce(
  history: ChatMessage[],
  event: string,
  data: unknown,
  setThreadId: (id: string) => void,
): ChatMessage[] {
  const next = history.map((m) =>
    typeof m.content === 'string'
      ? m
      : { ...m, content: [...m.content] },
  );
  const blocks = lastAssistantBlocks(next);
  if (!blocks) return history;

  switch (event) {
    case 'message_start': {
      const d = data as SSEMessageStart;
      setThreadId(d.thread_id);
      return next;
    }
    case 'followup_start': {
      // The backend's text-only "what's next" turn after tools ran. Its text
      // belongs in a fresh assistant bubble (tool_result user messages sit
      // between it and the planning bubble), so open a new assistant message
      // for the upcoming text_delta events to flow into.
      next.push({ role: 'assistant', content: [] });
      return next;
    }
    case 'text_delta': {
      const d = data as SSETextDelta;
      const trailing = blocks[blocks.length - 1];
      if (trailing?.type === 'text') {
        const updated: TextBlock = { ...trailing, text: trailing.text + d.delta };
        blocks[blocks.length - 1] = updated;
      } else {
        blocks.push({ type: 'text', text: d.delta });
      }
      return next;
    }
    case 'tool_use': {
      const d = data as SSEToolUse;
      blocks.push({
        type: 'tool_use',
        id: d.id,
        name: d.name,
        input: d.input,
        ui_status: 'pending',
      });
      return next;
    }
    case 'tool_running': {
      const d = data as SSEToolRunning;
      patchToolUse(blocks, d.tool_use_id, { ui_status: 'running' });
      return next;
    }
    case 'tool_awaiting_approval': {
      const d = data as SSEToolAwaitingApproval;
      patchToolUse(blocks, d.tool_use_id, {
        ui_status: 'awaiting_approval',
        ui_cost_preview: d.cost_preview,
      });
      return next;
    }
    case 'tool_completed': {
      const d = data as SSEToolCompleted;
      patchToolUse(blocks, d.tool_use_id, {
        ui_status: 'completed',
        ui_result: d.result,
      });
      next.push({
        role: 'user',
        content: [
          {
            type: 'tool_result',
            tool_use_id: d.tool_use_id,
            content: JSON.stringify(d.result),
            is_error: false,
          },
        ],
      });
      return next;
    }
    case 'tool_failed': {
      const d = data as SSEToolFailed;
      patchToolUse(blocks, d.tool_use_id, {
        ui_status: 'failed',
        ui_error: d.error,
      });
      next.push({
        role: 'user',
        content: [
          {
            type: 'tool_result',
            tool_use_id: d.tool_use_id,
            content: JSON.stringify({ error: d.error }),
            is_error: true,
          },
        ],
      });
      return next;
    }
    case 'error': {
      const d = data as SSEError;
      blocks.push({ type: 'text', text: `\n\nError: ${d.message}` });
      return next;
    }
    case 'done': {
      const d = data as SSEDone;
      setThreadId(d.thread_id);
      // A tool still 'pending' once the turn is done was stranded behind the
      // approval gate (the dispatch loop stops at the first money tool). It
      // never ran and never will — resolve it to 'cancelled' so it doesn't
      // linger as a button-less "Queued" card.
      for (let i = 0; i < blocks.length; i++) {
        const b = blocks[i];
        if (b && b.type === 'tool_use' && (b.ui_status === 'pending' || b.ui_status === undefined)) {
          blocks[i] = {
            ...b,
            ui_status: 'cancelled',
            ui_error: {
              code: 'gate_superseded',
              message: 'Not run — a cost-approval step took priority.',
            },
          };
        }
      }
      return next;
    }
    default:
      return next;
  }
}

function patchToolUse(
  blocks: ContentBlock[],
  toolUseId: string,
  patch: Partial<ToolUseBlock>,
): void {
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (b && b.type === 'tool_use' && b.id === toolUseId) {
      blocks[i] = { ...b, ...patch };
      return;
    }
  }
}
function patchToolUseInContent(
  content: ContentBlock[],
  toolUseId: string,
): ContentBlock[] {
  return content.map((b) => {
    if (b.type !== 'tool_use' || b.id !== toolUseId) return b;
    if (b.ui_status !== 'awaiting_approval') return b;
    return {
      ...b,
      ui_status: 'cancelled',
      ui_error: {
        code: 'operator_cancelled',
        message: 'Operator cancelled the cost-approval prompt.',
      },
    };
  });
}
